import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../api";
import { toast } from "sonner";
import { Plus, Trash2, X, Percent, ChevronDown, ChevronRight, Sparkles, History as HistoryIcon } from "lucide-react";
import HistoryDrawer, { ImportCsvButton } from "../../components/HistoryDrawer";
import { useSortedList } from "../../components/useSortedList";
import { useConfirm } from "../../components/Confirm";
import { useDataVersion } from "../../hooks/useAppShell";

const YE_MUSIC_PARTY = "YE Music (Label)";

export default function RoyaltySplits({ user }) {
  const [entries, setEntries] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [releases, setReleases] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [historyFor, setHistoryFor] = useState(null);
  const loc = useLocation();
  const highlightId = new URLSearchParams(loc.search).get("highlight");
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="row-royalty-${highlightId}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("entry-highlight"); setTimeout(() => el.classList.remove("entry-highlight"), 2600); }
    }, 350);
    return () => clearTimeout(t);
  }, [highlightId, entries]);

  const SORTABLE = [{ key: "song_title", label: "Song Title" }, { key: "status", label: "Status" }];
  const { sorted: sortedEntries, SortBar, sortBarProps, Pagination, paginationProps } = useSortedList(entries, SORTABLE);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.entries.list("royalty_splits");
      setEntries(r.entries || []); setVersion(r.version || 0);
      setReleases(await api.linkedReleases());
    } catch (e) { toast.error(String(e.message || e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const globalVersion = useDataVersion();
  useEffect(() => { if (globalVersion > 0) load(); }, [globalVersion, load]);

  const create = async (data) => {
    try { await api.entries.create("royalty_splits", data); toast.success("Split saved"); setShowAdd(false); load(); }
    catch (e) { toast.error(String(e.message || e)); }
  };
  const remove = (id) => {
    confirm(
      "This royalty split will move to Recently Deleted. You can restore it or delete it permanently within 15 days.",
      async () => {
        try { await api.entries.remove("royalty_splits", id); toast.success("Moved to Recently Deleted"); load(); }
        catch (e) { toast.error(String(e.message || e)); }
      },
      { title: "Delete this split?" }
    );
  };
  const setStatus = async (id, status) => {
    try { await api.entries.status("royalty_splits", id, status, version); load(); }
    catch (e) { toast.error(String(e.message || e)); load(); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Department · Admin only</div>
          <h1 style={{ fontSize: 30, margin: "4px 0 0 0" }}>Royalty Splits</h1>
          <p className="muted" style={{ marginTop: 8, fontSize: 12, maxWidth: 640 }}>
            Splits are per song. Master and Publishing rights are tracked separately. Adding a linked song auto-pulls credited parties from Released Songs.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="add-royalty-btn"><Plus size={14} /> New split</button>
      </div>

      {showAdd && (
        <RoyaltyForm releases={releases} onCancel={() => setShowAdd(false)} onSubmit={create} />
      )}

      <div className="panel" style={{ overflow: "hidden" }}>
        {loading ? <div style={{ padding: 20 }} className="muted">Loading…</div> :
         entries.length === 0 ? <div style={{ padding: 40, textAlign: "center" }} className="muted">No splits yet. Add one to get started.</div> :
        (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: 12 }}>
              <SortBar {...sortBarProps} />
              <span className="muted" style={{ fontSize: 11 }} data-testid="entry-count">{paginationProps.total} split{paginationProps.total === 1 ? "" : "s"}</span>
            </div>
            {sortedEntries.map(e => {
              const isOpen = expanded[e.id];
              const master = e.data.master_split_rows || [];
              const pub = e.data.publishing_split_rows || [];
              return (
                <div key={e.id} data-testid={`row-royalty-${e.id}`} style={{ borderBottom: "1px solid var(--border)" }}>
                  <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} onClick={() => setExpanded(x => ({ ...x, [e.id]: !x[e.id] }))}>
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{e.data.song_title || "(untitled)"}</div>
                      <div className="muted" style={{ fontSize: 11 }}>
                        {master.length} master row{master.length !== 1 && "s"} · {pub.length} publishing row{pub.length !== 1 && "s"}
                      </div>
                    </div>
                    <select className="input" style={{ maxWidth: 130 }} value={e.data.status || "Draft"} onClick={(ev) => ev.stopPropagation()} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`status-${e.id}`}>
                      <option>Draft</option><option>Confirmed</option>
                    </select>
                    <button className="btn" onClick={(ev) => { ev.stopPropagation(); setHistoryFor(e.id); }} data-testid={`history-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }} title="Change history"><HistoryIcon size={12} /></button>
                    <button className="btn btn-danger" onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} data-testid={`del-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}><Trash2 size={12} /></button>
                  </div>
                  {isOpen && (
                    <div style={{ padding: "0 18px 18px 42px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                      <SplitTable title="Master Rights" rows={master} />
                      <SplitTable title="Publishing Rights" rows={pub} />
                    </div>
                  )}
                </div>
              );
            })}
            <Pagination {...paginationProps} />
          </div>
        )}
      </div>
      {historyFor && <HistoryDrawer entryId={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

function SplitTable({ title, rows }) {
  const total = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
  const bal = 100 - total;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <div className="muted" style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>{title}</div>
        <span className={`badge ${bal === 0 ? "badge-mint" : "badge-warn"}`}>{total}% allocated{bal !== 0 && ` (${bal > 0 ? "+" : ""}${bal}% left)`}</span>
      </div>
      {rows.length === 0 ? <div className="muted" style={{ fontSize: 12, padding: 8 }}>No parties.</div> :
        <table>
          <thead><tr><th>Party</th><th>Role</th><th style={{ textAlign: "right" }}>%</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td className="muted" style={{ fontSize: 12 }}>{r.role}</td>
                <td style={{ textAlign: "right", fontFamily: "Space Grotesk", color: "var(--mint)" }}>{r.percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    </div>
  );
}

function RoyaltyForm({ releases, onCancel, onSubmit }) {
  const [songTitle, setSongTitle] = useState("");
  const [linkedRelease, setLinkedRelease] = useState("");
  const [master, setMaster] = useState([{ name: YE_MUSIC_PARTY, role: "Label", percent: 50 }]);
  const [pub, setPub] = useState([{ name: YE_MUSIC_PARTY, role: "Publisher", percent: 50 }]);

  const applyLinked = (id) => {
    setLinkedRelease(id);
    const r = releases.find(x => x.id === id);
    if (!r) return;
    setSongTitle(r.title);
    // Auto-populate parties: split evenly minus YE Music
    if (r.artist) {
      const names = r.artist.split(",").map(s => s.trim()).filter(Boolean);
      const yeShare = 50;
      const perArtist = names.length ? Math.floor((100 - yeShare) / names.length) : 0;
      const remainder = 100 - yeShare - perArtist * names.length;
      const artistRows = names.map((n, i) => ({ name: n, role: "Vocalist", percent: perArtist + (i === 0 ? remainder : 0) }));
      setMaster([{ name: YE_MUSIC_PARTY, role: "Label", percent: yeShare }, ...artistRows]);
      setPub([{ name: YE_MUSIC_PARTY, role: "Publisher", percent: yeShare }, ...artistRows.map(x => ({ ...x, role: "Songwriter" }))]);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!linkedRelease) return;
    onSubmit({
      song_title: songTitle,
      linked_release_id: linkedRelease,
      master_split_rows: master.map(r => ({ ...r, percent: Number(r.percent) || 0 })),
      publishing_split_rows: pub.map(r => ({ ...r, percent: Number(r.percent) || 0 })),
    });
  };

  return (
    <form onSubmit={submit} className="panel" style={{ padding: 22, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <h3 style={{ fontSize: 16 }}>New royalty split</h3>
        <button type="button" className="btn" onClick={onCancel}><X size={14} /></button>
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 12 }}>
          <span className="muted" style={{ display: "block", marginBottom: 5 }}>Linked release / song *</span>
          <select className="input" required value={linkedRelease} onChange={(e) => applyLinked(e.target.value)} data-testid="field-linked-release">
            <option value="">{releases.length === 0 ? "No releases yet — add via Release Planning or Released Songs first" : "Select release…"}</option>
            {releases.map(r => (
              <option key={r.id + r.source} value={r.id}>{r.title}{r.artist && ` — ${r.artist}`} · {r.status}</option>
            ))}
          </select>
          {linkedRelease && <div className="muted" style={{ fontSize: 11, marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}><Sparkles size={11} /> Parties auto-populated below — edit as needed.</div>}
        </label>
      </div>

      <SplitEditor title="Master Rights" rows={master} setRows={setMaster} />
      <div style={{ height: 12 }} />
      <SplitEditor title="Publishing Rights" rows={pub} setRows={setPub} />

      <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" data-testid="submit-royalty">Save split</button>
      </div>
    </form>
  );
}

function SplitEditor({ title, rows, setRows }) {
  const total = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
  const bal = 100 - total;
  const upd = (i, k, v) => setRows(rows.map((r, j) => i === j ? { ...r, [k]: v } : r));
  const add = () => setRows([...rows, { name: "", role: "Vocalist", percent: 0 }]);
  const remove = (i) => setRows(rows.filter((_, j) => j !== i));

  return (
    <div style={{ padding: 14, background: "var(--panel2)", borderRadius: 10, border: "1px solid var(--border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Percent size={13} style={{ color: "var(--mint)" }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        </div>
        <span className={`badge ${bal === 0 ? "badge-mint" : "badge-warn"}`}>{total}%{bal !== 0 && ` (${bal > 0 ? "+" : ""}${bal}% left)`}</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 90px 40px", gap: 8, marginBottom: 8 }}>
          <input className="input" placeholder="Party name" value={r.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`${title}-name-${i}`} />
          <select className="input" value={r.role} onChange={(e) => upd(i, "role", e.target.value)}>
            {["Vocalist","Producer","Songwriter","Lyricist","Composer","Mix Engineer","Publisher","Label","Featured Artist","Other"].map(x => <option key={x}>{x}</option>)}
          </select>
          <input className="input" type="number" step="0.01" value={r.percent} onChange={(e) => upd(i, "percent", e.target.value)} data-testid={`${title}-pct-${i}`} />
          <button type="button" className="btn btn-danger" onClick={() => remove(i)} style={{ padding: "5px 9px" }}><Trash2 size={12} /></button>
        </div>
      ))}
      <button type="button" className="btn" onClick={add} style={{ fontSize: 12 }}><Plus size={12} /> Add party</button>
    </div>
  );
}
